import { useEffect, useRef, useState, type ReactElement } from "react";
import { Group, Line, Rect } from "react-konva";
import { resolveFurniture } from "../../lib/furniture/resolve";
import type {
  CollaboratorState,
  PresenceCanvasView,
} from "../../lib/sharedRoomTypes";
import type { FitInfo } from "../../lib/useFitToRoom";
import type { Furniture, Opening, WallMount } from "../../lib/types";

/**
 * Konva overlay that renders live drag previews ("ghosts") for any
 * item another collaborator is currently dragging. While a remote user
 * has the drag handle, their item's Firestore-stored position is stale
 * (we suppress per-frame writes during a drag — see `diffOptions` in
 * `sharedRoomTypes.ts`); the moving position streams through RTDB
 * `presence/{roomId}/{uid}/{tabId}/dragging` at ~30 Hz instead.
 *
 * This component reads that stream and paints the item at the live
 * position with the collaborator's color outline, the same way Figma
 * shows other users' drag operations. Without this overlay, the
 * dragged piece appears frozen until the remote user releases — a
 * critical UX gap.
 *
 * Like {@link CollaboratorCursors}, we tween toward the latest RTDB
 * sample at 60 Hz with a short ease so motion stays smooth despite the
 * lower wire rate.
 */

interface DragAnim {
  /** Last sample we received from RTDB. */
  targetX: number;
  targetY: number;
  targetRotation: number;
  /** Interpolated position currently being rendered. */
  currentX: number;
  currentY: number;
  currentRotation: number;
  /** Time of the last `target` change (perf clock). */
  lastChangeAt: number;
  /** The kind/id of the dragged item, captured at sample-receive time so
   *  we can detect mid-flight kind switches and snap rather than tween. */
  kind: string;
}

const INTERPOLATION_DURATION_MS = 80;

interface DesignProps {
  collaborators: ReadonlyMap<string, CollaboratorState>;
  furniture: Furniture[];
  fit: FitInfo;
  view: Extract<PresenceCanvasView, "design">;
}

/** Drag preview overlay for the top-down design view. Reads furniture
 *  dimensions from the local `furniture` array (collaborators don't
 *  resize during a drag, only translate/rotate). */
export function CollaboratorDragPreviewsDesign({
  collaborators,
  furniture,
  fit,
}: DesignProps) {
  const animRef = useRef<Map<string, DragAnim>>(new Map());
  const [, forceRender] = useState(0);

  // Sync targets when collaborators change.
  useEffect(() => {
    const next = new Map<string, DragAnim>();
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    for (const [uid, c] of collaborators.entries()) {
      const drag = c.dragging;
      if (!drag || drag.kind !== "furniture") continue;
      const key = `${drag.kind}:${drag.id}`;
      const prior = animRef.current.get(uid);
      const reuse = prior && prior.kind === key;
      next.set(uid, {
        targetX: drag.x,
        targetY: drag.y,
        targetRotation: drag.rotation,
        currentX: reuse ? prior!.currentX : drag.x,
        currentY: reuse ? prior!.currentY : drag.y,
        currentRotation: reuse ? prior!.currentRotation : drag.rotation,
        lastChangeAt: now,
        kind: key,
      });
    }
    animRef.current = next;
    forceRender((x) => x + 1);
  }, [collaborators]);

  // rAF tween loop while there's at least one preview active.
  useEffect(() => {
    if (animRef.current.size === 0) return;
    let raf = 0;
    function tick() {
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      let stillAnimating = false;
      for (const a of animRef.current.values()) {
        const t = Math.min(
          1,
          (now - a.lastChangeAt) / INTERPOLATION_DURATION_MS
        );
        const e = 1 - Math.pow(1 - t, 3);
        a.currentX = a.currentX + (a.targetX - a.currentX) * e;
        a.currentY = a.currentY + (a.targetY - a.currentY) * e;
        // Angles tween linearly through the shortest arc; collaborators
        // don't twirl past 180° in a single drag step in practice.
        const dr = shortestAngleDelta(a.currentRotation, a.targetRotation);
        a.currentRotation = a.currentRotation + dr * e;
        if (
          Math.abs(a.targetX - a.currentX) > 0.01 ||
          Math.abs(a.targetY - a.currentY) > 0.01 ||
          Math.abs(shortestAngleDelta(a.currentRotation, a.targetRotation)) >
            0.05
        ) {
          stillAnimating = true;
        }
      }
      forceRender((x) => x + 1);
      if (stillAnimating) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  });

  if (animRef.current.size === 0) return null;

  const items: ReactElement[] = [];
  for (const [uid, c] of collaborators.entries()) {
    const drag = c.dragging;
    if (!drag || drag.kind !== "furniture") continue;
    const anim = animRef.current.get(uid);
    if (!anim) continue;
    // Look up dimensions/color from the local copy. If the piece doesn't
    // exist locally yet (e.g. just created on the remote and our snapshot
    // hasn't arrived) we skip rather than guess — the gap is one frame.
    const f = furniture.find((x) => x.id === drag.id);
    if (!f) continue;
    const center = fit.pointToPx({ x: anim.currentX, y: anim.currentY });
    // Ghost follows the piece's true footprint (not its bounding box).
    const footprint = resolveFurniture(f).footprint;
    const flat: number[] = [];
    for (const p of footprint) flat.push(p.x * fit.scale, p.y * fit.scale);
    items.push(
      <Group
        key={`drag-${uid}`}
        x={center.x}
        y={center.y}
        rotation={anim.currentRotation}
        listening={false}
      >
        <Line
          points={flat}
          closed
          fill={f.color}
          opacity={0.55}
          stroke={c.color}
          strokeWidth={2.5}
          dash={[6, 4]}
          lineJoin="round"
        />
      </Group>
    );
  }
  return <>{items}</>;
}

interface WallProjection {
  scale: number;
  x0: number;
  y0: number;
  /** Wall length in inches. */
  w: number;
  /** Ceiling height in inches. */
  h: number;
}

interface WallProps {
  collaborators: ReadonlyMap<string, CollaboratorState>;
  mounts: WallMount[];
  openings: Opening[];
  fit: WallProjection;
  activeWallIndex: number | null;
  interiorWallId?: string | null;
  interiorSegment?: number | null;
}

/** Drag preview overlay for the wall-elevation view. Renders ghosts of
 *  mounts and openings other users are dragging. The wall view uses a
 *  custom (alongWall, fromFloor) projection rather than `useFitToRoom`. */
export function CollaboratorDragPreviewsWall({
  collaborators,
  mounts,
  openings,
  fit,
  activeWallIndex,
  interiorWallId = null,
  interiorSegment = null,
}: WallProps) {
  const animRef = useRef<Map<string, DragAnim>>(new Map());
  const [, forceRender] = useState(0);

  useEffect(() => {
    const next = new Map<string, DragAnim>();
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    for (const [uid, c] of collaborators.entries()) {
      const drag = c.dragging;
      if (!drag) continue;
      if (drag.kind !== "mount" && drag.kind !== "opening") continue;
      if (interiorWallId) {
        if (drag.interiorWallId !== interiorWallId || drag.interiorSegment !== interiorSegment) {
          continue;
        }
      } else if (drag.interiorWallId || drag.wallIndex !== activeWallIndex) {
        continue;
      }
      const key = `${drag.kind}:${drag.id}`;
      const prior = animRef.current.get(uid);
      const reuse = prior && prior.kind === key;
      next.set(uid, {
        targetX: drag.alongWall,
        targetY: drag.fromFloor,
        targetRotation: 0,
        currentX: reuse ? prior!.currentX : drag.alongWall,
        currentY: reuse ? prior!.currentY : drag.fromFloor,
        currentRotation: 0,
        lastChangeAt: now,
        kind: key,
      });
    }
    animRef.current = next;
    forceRender((x) => x + 1);
  }, [collaborators, activeWallIndex, interiorWallId, interiorSegment]);

  useEffect(() => {
    if (animRef.current.size === 0) return;
    let raf = 0;
    function tick() {
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      let stillAnimating = false;
      for (const a of animRef.current.values()) {
        const t = Math.min(
          1,
          (now - a.lastChangeAt) / INTERPOLATION_DURATION_MS
        );
        const e = 1 - Math.pow(1 - t, 3);
        a.currentX = a.currentX + (a.targetX - a.currentX) * e;
        a.currentY = a.currentY + (a.targetY - a.currentY) * e;
        if (
          Math.abs(a.targetX - a.currentX) > 0.01 ||
          Math.abs(a.targetY - a.currentY) > 0.01
        ) {
          stillAnimating = true;
        }
      }
      forceRender((x) => x + 1);
      if (stillAnimating) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  });

  if (animRef.current.size === 0) return null;

  const items: ReactElement[] = [];
  for (const [uid, c] of collaborators.entries()) {
    const drag = c.dragging;
    if (!drag) continue;
    if (drag.kind !== "mount" && drag.kind !== "opening") continue;
    if (interiorWallId) {
      if (drag.interiorWallId !== interiorWallId || drag.interiorSegment !== interiorSegment) {
        continue;
      }
    } else if (drag.interiorWallId || drag.wallIndex !== activeWallIndex) {
      continue;
    }
    const anim = animRef.current.get(uid);
    if (!anim) continue;
    const item =
      drag.kind === "mount"
        ? mounts.find((m) => m.id === drag.id)
        : openings.find((o) => o.id === drag.id);
    if (!item) continue;
    const cx = fit.x0 + anim.currentX * fit.scale;
    const cy = fit.y0 + fit.h * fit.scale - anim.currentY * fit.scale;
    const wPx = item.width * fit.scale;
    const hPx = item.height * fit.scale;
    items.push(
      <Group key={`drag-${uid}`} x={cx} y={cy} listening={false}>
        <Rect
          x={-wPx / 2}
          y={-hPx / 2}
          width={wPx}
          height={hPx}
          fill={drag.kind === "mount" ? (item as WallMount).color : "#dfeaf6"}
          opacity={0.55}
          stroke={c.color}
          strokeWidth={2.5}
          dash={[6, 4]}
          cornerRadius={2}
        />
      </Group>
    );
  }
  return <>{items}</>;
}

interface BuildProps {
  collaborators: ReadonlyMap<string, CollaboratorState>;
  fit: FitInfo;
}

/** Drag preview overlay for the build (room-editor) view. Currently
 *  only handles vertex drags — wall slides repaint the full polygon
 *  via Firestore, which is fast enough that a per-frame ghost adds
 *  little value. */
export function CollaboratorDragPreviewsBuild({
  collaborators,
  fit,
}: BuildProps) {
  const items: ReactElement[] = [];
  for (const c of collaborators.values()) {
    const drag = c.dragging;
    if (!drag || drag.kind !== "roomVertex") continue;
    const p = fit.pointToPx({ x: drag.x, y: drag.y });
    items.push(
      <Group key={`vd-${c.uid}`} x={p.x} y={p.y} listening={false}>
        <Rect
          x={-12}
          y={-12}
          width={24}
          height={24}
          stroke={c.color}
          strokeWidth={2.5}
          cornerRadius={12}
          opacity={0.85}
        />
      </Group>
    );
  }
  if (items.length === 0) return null;
  return <>{items}</>;
}

/** Smallest signed angle (degrees) to add to `from` to reach `to`. */
function shortestAngleDelta(from: number, to: number): number {
  const diff = ((to - from + 540) % 360) - 180;
  return diff;
}
